import MultiSelect from '@/components/global/multi-select';
import Select from '@/components/global/select';
import { ProfileFiltersValues } from '@/lib/models/client.types';
import { useI18n } from '@/locales/client';

export const MarketplaceFilters = ({
  profileFiltersValues,
  filters,
  setFilters,
}: {
  profileFiltersValues: ProfileFiltersValues;
  filters: {
    areasOfLaw: string;
    city: string;
    stateProvince: string;
    country: string;
    language: string;
  };
  setFilters: (filters: {
    areasOfLaw: string;
    city: string;
    stateProvince: string;
    country: string;
    language: string;
  }) => void;
}) => {
  const t = useI18n();

  return (
    <div className='space-y-4'>
      <Select
        placeholder={t('marketplace.filters.areasOfLaw')}
        options={[
          { label: t('marketplace.filters.all'), value: 'all' },
          ...profileFiltersValues.areasOfLaw.map((areaOfLaw) => ({
            label: areaOfLaw,
            value: areaOfLaw,
          })),
        ]}
        onValueChange={(value) => setFilters({ ...filters, areasOfLaw: value })}
        value={filters.areasOfLaw}
      />

      <MultiSelect
        placeholder={t('marketplace.filters.language')}
        options={[
          { label: t('marketplace.filters.all'), value: 'all' },
          ...profileFiltersValues.languages.map((language) => ({
            label: language,
            value: language,
          })),
        ]}
        onChange={(value) => {
          const hasAll = value.includes('all');
          const previouslyHadAll =
            filters.language === 'all' ||
            filters.language.split(',').includes('all');

          // If "all" was just selected and other items are selected, keep only "all"
          if (hasAll && !previouslyHadAll && value.length > 1) {
            setFilters({ ...filters, language: 'all' });
          }
          // If something else was selected while "all" was already selected, remove "all"
          else if (previouslyHadAll && value.length > 1) {
            const newValues = value.filter((v) => v !== 'all');
            setFilters({ ...filters, language: newValues.join(',') });
          }
          // Default case - just set whatever was selected
          else {
            setFilters({ ...filters, language: value.join(',') });
          }
        }}
        defaultValue={
          filters.language.split(',').length > 0
            ? filters.language.split(',')
            : []
        }
      />

      <Select
        placeholder={t('marketplace.filters.country')}
        options={[
          { label: t('marketplace.filters.all'), value: 'all' },
          ...profileFiltersValues.countries.map((country) => ({
            label: country,
            value: country,
          })),
        ]}
        onValueChange={(value) => setFilters({ ...filters, country: value })}
        value={filters.country}
      />
      <Select
        placeholder={t('marketplace.filters.stateProvince')}
        options={[
          { label: t('marketplace.filters.all'), value: 'all' },
          ...profileFiltersValues.stateProvinces.map((stateProvince) => ({
            label: stateProvince,
            value: stateProvince,
          })),
        ]}
        onValueChange={(value) =>
          setFilters({
            ...filters,
            stateProvince: value,
          })
        }
        value={filters.stateProvince}
      />
      <Select
        placeholder={t('marketplace.filters.city')}
        options={[
          { label: t('marketplace.filters.all'), value: 'all' },
          ...profileFiltersValues.cities.map((city) => ({
            label: city,
            value: city,
          })),
        ]}
        onValueChange={(value) => setFilters({ ...filters, city: value })}
        value={filters.city}
      />
    </div>
  );
};
